/**
 * Logiblocks graph layout.
 *
 * The graph is a tree (each node has at most one parent in its level), with
 * an orthogonal nesting relation: SUBGRAPH_PARENT nodes (e.g. Loop) host an
 * inner subgraph addressed by `parentId`. Layout proceeds in three stages:
 *
 *   1. Group nodes by `parentId` so each level is a separate problem.
 *   2. Lay out subgraphs bottom-up. After laying out a subgraph's contents,
 *      `calculateBoundingBox` measures the result and stores that as the
 *      SUBGRAPH_PARENT's intrinsic size — outer levels then treat the
 *      SUBGRAPH_PARENT as a single node of that size.
 *   3. Lay out each level using a contour-based packer (`layoutLevel`).
 *
 * # Why contour-based packing
 *
 * A naive layout picks a fixed sibling spacing regardless of what's below;
 * that collides when one branch fans out and the other doesn't. Allocating
 * each child a slot equal to the widest part of its subtree avoids overlap
 * but is too conservative — a subtree whose widest descendant is several
 * rows below still reserves that full width up at the top, pushing nearby
 * siblings far apart even when there's nothing for them to actually
 * collide with.
 *
 * The contour algorithm tracks two arrays per subtree, indexed by depth:
 *
 *   left[d]  = leftmost X at depth d in the subtree (relative to root)
 *   right[d] = rightmost X at depth d
 *
 * Picture each subtree as a silhouette. To place sibling B to the right of
 * sibling A, walk A's right silhouette and B's left silhouette in lockstep
 * and find the depth where they come closest; B shifts right by exactly
 * enough to leave SIBLING_SUBTREE_GAP at that pinch point. At depths where
 * one side has no node, there's no constraint — that's how "narrow top,
 * wide bottom" subtrees can interlock with neighbors. Their tops touch but
 * their bottoms don't.
 *
 * After all children of a node are packed, the band is shifted so its
 * outer edges are symmetric around 0, re-centering children under their
 * parent. The parent's own contour is the union of its children's contours
 * (each shifted by its placement offset), with the parent's own ±width/2
 * prepended at depth 0.
 *
 * Cost is O(n²) worst case for a deep narrow tree but O(n) in practice for
 * workflow trees. Buchheim et al. (2002) give a true O(n) variant using
 * thread pointers; we don't need it at our scale and prefer the readable
 * form.
 *
 * # Invariants
 *
 *   - Y is determined by the depth chain: each node's Y is its parent's Y
 *     plus the parent's height plus CHILD_NODE_Y_OFFSET − NODE_HEIGHT. With
 *     uniform-height nodes this collapses to "all nodes at the same BFS
 *     depth share a Y."
 *   - Children are sorted by `edgeOrder` on the incoming edge before packing,
 *     fixing left-to-right sibling order.
 *   - Children are centered under their parent (post-pack centering step).
 *   - SUBGRAPH_PARENT nodes render at their measured size — `data.width` and
 *     `data.height` are written in the final materialization pass.
 */

import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { getLBNodeInfo } from './nodes/registry';
import { OutputType, type ReactFlowEdge, type ReactFlowNode } from './types';
import {
  CHILD_NODE_Y_OFFSET,
  NODE_HEIGHT,
  NODE_WIDTH,
  SIBLING_SUBTREE_GAP,
  SUBGRAPH_HEADER_OVERFLOW,
  SUBGRAPH_PADDING_X,
  SUBGRAPH_PADDING_Y,
} from './styles';

/**
 * Groups nodes by their parentId.
 * Returns a map where undefined key contains root-level nodes.
 */
function groupNodesByParent(
  nodes: ReactFlowNode[]
): Map<string | undefined, ReactFlowNode[]> {
  const result = new Map<string | undefined, ReactFlowNode[]>();

  for (const node of nodes) {
    const parentId = node.parentId;
    const group = result.get(parentId) ?? [];
    group.push(node);
    result.set(parentId, group);
  }

  return result;
}

/**
 * Filters edges to only those connecting nodes in the given set.
 */
function filterEdgesForNodes(
  edges: ReactFlowEdge[],
  nodeIds: Set<string>
): ReactFlowEdge[] {
  return edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
}

// Vertical offset between a subgraph parent's container top and its
// inner-root node. The inner-root must land on the same outer row as the
// subgraph parent's outer-level nephews. Since the container sits
// SUBGRAPH_HEADER_OVERFLOW pixels below the subgraph parent's header (the
// header overflows above the container), the inner shift is one row
// MINUS that overflow.
const INNER_ROOT_Y_OFFSET = CHILD_NODE_Y_OFFSET - SUBGRAPH_HEADER_OVERFLOW;

/**
 * Returns the default size for a node based on its type.
 * SUBGRAPH_PARENT nodes get minimum subgraph bounds, others get standard node size.
 */
function getDefaultNodeSize(node: ReactFlowNode): {
  width: number;
  height: number;
} {
  const nodeInfo = getLBNodeInfo(node.data as LBNodeData);
  if (nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT) {
    return {
      width: NODE_WIDTH + SUBGRAPH_PADDING_X * 2,
      height: INNER_ROOT_Y_OFFSET + SUBGRAPH_PADDING_Y,
    };
  }
  return { width: NODE_WIDTH, height: NODE_HEIGHT };
}

/**
 * Calculates bounding box of positioned nodes.
 * Returns { width, height } with padding applied.
 */
function calculateBoundingBox(
  positions: Map<string, { x: number; y: number }>,
  nodeSizes: Map<string, { width: number; height: number }>,
  nodes: ReactFlowNode[]
): { width: number; height: number } {
  // Y offset applied by centerPositions: lands the inner subgraph's first
  // row on the same row grid that outer-level siblings use.
  const yOffset = INNER_ROOT_Y_OFFSET;

  if (positions.size === 0) {
    return {
      width: NODE_WIDTH + SUBGRAPH_PADDING_X * 2,
      height: yOffset + SUBGRAPH_PADDING_Y,
    };
  }

  // Build lookup for nodes by ID
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  let minX = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const [nodeId, pos] of Array.from(positions)) {
    const node = nodeById.get(nodeId)!;
    const size = nodeSizes.get(nodeId) ?? getDefaultNodeSize(node);
    // Node origin is top-center, so left edge is at x - width/2
    const leftEdge = pos.x - size.width / 2;
    const rightEdge = pos.x + size.width / 2;
    const bottomEdge = pos.y + size.height;

    minX = Math.min(minX, leftEdge);
    maxX = Math.max(maxX, rightEdge);
    maxY = Math.max(maxY, bottomEdge);
  }

  const contentWidth = maxX - minX;
  const contentHeight = maxY;

  return {
    width: contentWidth + SUBGRAPH_PADDING_X * 2,
    height: contentHeight + yOffset + SUBGRAPH_PADDING_Y,
  };
}

/**
 * Shifts positions so the content is centered within the bounding box.
 */
function centerPositions(
  positions: Map<string, { x: number; y: number }>,
  nodeSizes: Map<string, { width: number; height: number }>,
  containerWidth: number
): void {
  if (positions.size === 0) return;

  // Find current bounds
  let minX = Infinity;
  let maxX = -Infinity;

  for (const [nodeId, pos] of Array.from(positions)) {
    const size = nodeSizes.get(nodeId) ?? {
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    };
    minX = Math.min(minX, pos.x - size.width / 2);
    maxX = Math.max(maxX, pos.x + size.width / 2);
  }

  const contentWidth = maxX - minX;
  const offsetX = (containerWidth - contentWidth) / 2 - minX;

  // Shift all positions. The Y offset lands the inner subgraph's first row
  // on the same Y grid as outer-level siblings' children rows. Header-overflow
  // is subtracted because the subgraph parent's container top sits that far
  // below the header, and we measure inner-root from the container top.
  for (const [nodeId, pos] of Array.from(positions)) {
    positions.set(nodeId, {
      x: pos.x + offsetX,
      y: pos.y + INNER_ROOT_Y_OFFSET,
    });
  }
}

/**
 * Lays out a single graph level. Each subtree carries left/right contour
 * arrays so siblings can interlock at depths where they don't conflict —
 * a subtree's deep wide section doesn't push narrow siblings apart up top.
 */
function layoutLevel(
  levelNodes: ReactFlowNode[],
  levelEdges: ReactFlowEdge[],
  nodeSizes: Map<string, { width: number; height: number }>
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();

  if (levelNodes.length === 0) {
    return positions;
  }

  // Index nodes and edges once so inner loops are O(1) lookups.
  const nodeById = new Map<string, ReactFlowNode>();
  for (const node of levelNodes) {
    nodeById.set(node.id, node);
  }

  const hasIncoming = new Set<string>();
  const outgoingByNode = new Map<string, ReactFlowEdge[]>();
  for (const edge of levelEdges) {
    hasIncoming.add(edge.target);
    const bucket = outgoingByNode.get(edge.source);
    if (bucket) {
      bucket.push(edge);
    } else {
      outgoingByNode.set(edge.source, [edge]);
    }
  }
  // Sort each source's outgoing edges by edgeOrder once (left-to-right sibling order).
  for (const bucket of outgoingByNode.values()) {
    bucket.sort(
      (a, b) =>
        ((a.data?.edgeOrder as number) ?? 0) -
        ((b.data?.edgeOrder as number) ?? 0)
    );
  }

  // Root = first level node with no incoming edge from within this level.
  let root: ReactFlowNode | undefined;
  for (const node of levelNodes) {
    if (!hasIncoming.has(node.id)) {
      root = node;
      break;
    }
  }
  if (!root) {
    // Workflow graphs are always trees, so a level always has a root.
    // Reaching here means graph construction has gone terribly wrong.
    console.error('layoutLevel: No root node found');
    return positions;
  }

  function sizeOf(nodeId: string): { width: number; height: number } {
    const cached = nodeSizes.get(nodeId);
    if (cached) {
      return cached;
    }
    return getDefaultNodeSize(nodeById.get(nodeId)!);
  }

  function childrenOf(nodeId: string): string[] {
    const edges = outgoingByNode.get(nodeId);
    if (!edges) {
      return [];
    }
    const ids: string[] = new Array(edges.length);
    for (let i = 0; i < edges.length; i++) {
      ids[i] = edges[i].target;
    }
    return ids;
  }

  // Whether `nodeId` is a SUBGRAPH_PARENT — used to compensate for its
  // header sticking up above the container so it lands on the outer-row Y.
  function isSubgraphParent(nodeId: string): boolean {
    const node = nodeById.get(nodeId);
    if (!node) {
      return false;
    }
    const info = getLBNodeInfo(node.data as LBNodeData);
    return info?.outputType === OutputType.SUBGRAPH_PARENT;
  }

  // A laid-out subtree: each descendant's X relative to the subtree root,
  // plus the subtree's left/right contours indexed by depth from the root.
  type Subtree = {
    rel: Map<string, number>;
    left: number[];
    right: number[];
  };

  // Builds the subtree rooted at `nodeId`. Recursive — workflow trees are
  // small (depth ≤ ~30), so the call stack is fine.
  function buildSubtree(nodeId: string): Subtree {
    const size = sizeOf(nodeId);
    const halfWidth = size.width / 2;
    // A SUBGRAPH_PARENT spans multiple visual rows (its bounding box covers
    // its inner contents). Its self-contour repeats ±halfWidth across every
    // row it occupies so a sibling's deeper-row descendants can't slip
    // sideways into the SUBGRAPH_PARENT's column.
    //
    // Ceiling is conservative: a height that overshoots a row boundary
    // claims the next row too. Worse case is over-blocking by part of a row,
    // which only ever pushes siblings slightly farther apart — never lets
    // them collide.
    const selfRows = Math.max(1, Math.ceil(size.height / CHILD_NODE_Y_OFFSET));
    const selfLeft: number[] = new Array(selfRows).fill(-halfWidth);
    const selfRight: number[] = new Array(selfRows).fill(halfWidth);
    const kids = childrenOf(nodeId);

    if (kids.length === 0) {
      const rel = new Map<string, number>();
      rel.set(nodeId, 0);
      return { rel, left: selfLeft, right: selfRight };
    }

    const childTrees = kids.map(buildSubtree);

    // Pack children left-to-right. Each child shifts right just enough to
    // clear the running right contour of everything packed so far. At depths
    // where one side has no node, the contour stops constraining — that's
    // what lets unrelated subtrees interlock.
    const childOffsets: number[] = new Array(kids.length);
    const runningRight: number[] = [];
    for (let i = 0; i < childTrees.length; i++) {
      const ct = childTrees[i];
      let shift = 0;
      if (i > 0) {
        const cmpDepths = Math.min(runningRight.length, ct.left.length);
        for (let d = 0; d < cmpDepths; d++) {
          const need = runningRight[d] - ct.left[d] + SIBLING_SUBTREE_GAP;
          if (need > shift) {
            shift = need;
          }
        }
      }
      childOffsets[i] = shift;
      // Merge this child's right contour (shifted) into runningRight.
      for (let d = 0; d < ct.right.length; d++) {
        const v = ct.right[d] + shift;
        if (d < runningRight.length) {
          if (v > runningRight[d]) {
            runningRight[d] = v;
          }
        } else {
          runningRight.push(v);
        }
      }
    }

    // Center the children band under the parent: shift everything so the
    // midpoint of (leftmost child edge, rightmost child edge) lands at 0.
    let leftmostEdge = Infinity;
    let rightmostEdge = -Infinity;
    for (let i = 0; i < childTrees.length; i++) {
      const ct = childTrees[i];
      const leftEdge = childOffsets[i] + ct.left[0];
      const rightEdge = childOffsets[i] + ct.right[0];
      if (leftEdge < leftmostEdge) {
        leftmostEdge = leftEdge;
      }
      if (rightEdge > rightmostEdge) {
        rightmostEdge = rightEdge;
      }
    }
    const centerShift = -(leftmostEdge + rightmostEdge) / 2;
    for (let i = 0; i < kids.length; i++) {
      childOffsets[i] += centerShift;
    }

    // Assemble this subtree's relative positions and contours. Children
    // start at depth `selfRows` because the parent's own contour entries
    // already cover depths 0..selfRows-1.
    const rel = new Map<string, number>();
    rel.set(nodeId, 0);
    const left: number[] = selfLeft.slice();
    const right: number[] = selfRight.slice();
    for (let i = 0; i < kids.length; i++) {
      const ct = childTrees[i];
      const xShift = childOffsets[i];
      for (const [descId, descX] of ct.rel) {
        rel.set(descId, descX + xShift);
      }
      for (let d = 0; d < ct.left.length; d++) {
        const absD = d + selfRows;
        const lv = ct.left[d] + xShift;
        const rv = ct.right[d] + xShift;
        if (absD < left.length) {
          if (lv < left[absD]) {
            left[absD] = lv;
          }
          if (rv > right[absD]) {
            right[absD] = rv;
          }
        } else {
          left.push(lv);
          right.push(rv);
        }
      }
    }

    return { rel, left, right };
  }

  const rootSubtree = buildSubtree(root.id);

  // Y is parent's Y plus parent's height plus the standard offset. SUBGRAPH_PARENT
  // children get an extra push so their header (which renders above the
  // container) lands on the same Y as regular siblings.
  function assignY(nodeId: string, y: number): void {
    const x = rootSubtree.rel.get(nodeId)!;
    positions.set(nodeId, { x, y });
    const childY =
      y + sizeOf(nodeId).height + CHILD_NODE_Y_OFFSET - NODE_HEIGHT;
    for (const childId of childrenOf(nodeId)) {
      const offset = isSubgraphParent(childId) ? SUBGRAPH_HEADER_OVERFLOW : 0;
      assignY(childId, childY + offset);
    }
  }

  assignY(root.id, 0);

  return positions;
}

/**
 * Lays out the graph. SUBGRAPH_PARENT nodes are sized to fit their contents.
 * Returns new node objects with computed positions; input nodes are not mutated.
 */
export function calculateGraphLayout(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode[] {
  if (nodes.length === 0) return [];

  // Group nodes by parentId
  const nodesByParent = groupNodesByParent(nodes);

  // Track computed sizes for subgraph parent nodes
  const nodeSizes = new Map<string, { width: number; height: number }>();

  // Track final positions for all nodes
  const allPositions = new Map<string, { x: number; y: number }>();

  // Process subgraphs bottom-up: find all subgraph parents and process deepest first
  // A subgraph parent is a node that has children in nodesByParent
  const subgraphParentIds = Array.from(nodesByParent.keys()).filter(
    (k) => k !== undefined
  ) as string[];

  // Bottom-up order is enforced by processSubgraph: each call recurses into
  // its nested subgraphs first, so a SP's measured size is always available
  // before its outer level is laid out.
  const processedParents = new Set<string>();

  function processSubgraph(parentId: string): void {
    if (processedParents.has(parentId)) return;

    const children = nodesByParent.get(parentId) ?? [];
    const childIds = new Set(children.map((n) => n.id));

    // First, process any nested subgraphs within this subgraph
    for (const child of children) {
      if (nodesByParent.has(child.id)) {
        processSubgraph(child.id);
      }
    }

    // Now layout this subgraph's direct children
    const levelEdges = filterEdgesForNodes(edges, childIds);
    const levelPositions = layoutLevel(children, levelEdges, nodeSizes);

    // Defensive: workflow graphs are always trees, so layoutLevel should
    // always find a root. If we ever land here something has gone terribly
    // wrong upstream — bail rather than emit garbage positions, and let the
    // children fall through to the {x:0, y:0} default in the final pass.
    if (children.length > 0 && levelPositions.size === 0) {
      processedParents.add(parentId);
      return;
    }

    // Calculate bounding box and store parent size
    const bounds = calculateBoundingBox(levelPositions, nodeSizes, children);
    nodeSizes.set(parentId, bounds);

    // Center positions within the container and store them
    centerPositions(levelPositions, nodeSizes, bounds.width);
    for (const [nodeId, pos] of Array.from(levelPositions)) {
      allPositions.set(nodeId, pos);
    }

    processedParents.add(parentId);
  }

  // Process all subgraphs
  for (const parentId of subgraphParentIds) {
    processSubgraph(parentId);
  }

  // Layout root level
  const rootNodes = nodesByParent.get(undefined) ?? [];
  const rootNodeIds = new Set(rootNodes.map((n) => n.id));
  const rootEdges = filterEdgesForNodes(edges, rootNodeIds);
  const rootPositions = layoutLevel(rootNodes, rootEdges, nodeSizes);

  // If layoutLevel couldn't find a BFS starting point (no node without incoming
  // edges), return original nodes unchanged
  if (rootNodes.length > 0 && rootPositions.size === 0) {
    return nodes;
  }

  for (const [nodeId, pos] of Array.from(rootPositions)) {
    allPositions.set(nodeId, pos);
  }

  // Apply positions and sizes to nodes
  return nodes.map((node) => {
    const position = allPositions.get(node.id) ?? { x: 0, y: 0 };
    // Use computed size if available, otherwise use default (handles empty SUBGRAPH_PARENTs)
    const size = nodeSizes.get(node.id) ?? getDefaultNodeSize(node);
    const nodeInfo = getLBNodeInfo(node.data as LBNodeData);

    // Update width/height for SUBGRAPH_PARENT nodes
    if (nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT) {
      return {
        ...node,
        position,
        data: {
          ...node.data,
          width: size.width,
          height: size.height,
        },
      };
    }

    return {
      ...node,
      position,
    };
  });
}
